import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, AlertCircle, X, Copy, CheckCheck, Loader2 } from 'lucide-react';

const OpenAllLinksButton = ({ generatedLinks, linkType, label }) => {
  // Initialize all hooks unconditionally
  const [showLinkList, setShowLinkList] = useState(false);
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [copied, setCopied] = useState(false);
  const [openingLinks, setOpeningLinks] = useState(false);
  const [currentLinkIndex, setCurrentLinkIndex] = useState(0);
  const [openDelay, setOpenDelay] = useState(3); // Default 3 seconds delay
  const [maxLinks, setMaxLinks] = useState('all'); // 'all' or a number
  const [lastOpenedIndex, setLastOpenedIndex] = useState(0); // Track where we left off
  const modalRef = useRef(null);

  // Filter and count links safely
  const filteredLinks = React.useMemo(() => {
    if (!generatedLinks || !Array.isArray(generatedLinks)) {
      return [];
    }
    
    return generatedLinks.filter(company => 
      company && company.links && company.links[linkType]
    );
  }, [generatedLinks, linkType]);
  
  const linkCount = filteredLinks.length;

  // Safely handle modal visibility when shown
  useEffect(() => {
    if (showLinkList && modalRef.current) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        modalRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }, [showLinkList]);

  // No early return - just render an empty button if no links
  const handleShowLinks = () => {
    // Only proceed if we have links
    if (filteredLinks.length === 0) return;
    
    // Collect all the links of the specified type
    const links = filteredLinks.map(company => ({
      company: company.company,
      link: company.links[linkType].link,
      description: company.links[linkType].description,
      opened: false // Track which links have been opened
    }));
    
    setSelectedLinks(links);
    setShowLinkList(true);
    setCurrentLinkIndex(0);
    setOpeningLinks(false);
  };

  const handleCopyAllLinks = () => {
    const textToCopy = selectedLinks.map(item => 
      `${item.company}: ${item.link}`
    ).join('\n\n');
    
    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy links: ', err);
      });
  };

  // This function opens a single link
  const handleOpenSingle = (url, index) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    
    // Mark this link as opened
    if (index !== undefined) {
      const updatedLinks = [...selectedLinks];
      updatedLinks[index] = { ...updatedLinks[index], opened: true };
      setSelectedLinks(updatedLinks);
    }
  };

  // Rate-limited link opening process
  const startRateLimitedOpening = (continueFrom = null) => {
    if (selectedLinks.length === 0) return;

    const startIndex = continueFrom !== null ? continueFrom : 0;
    setOpeningLinks(true);
    setCurrentLinkIndex(startIndex);

    // Only reset unopened links
    const resetLinks = selectedLinks.map((link, idx) => ({
      ...link,
      opened: idx < startIndex ? true : false
    }));
    setSelectedLinks(resetLinks);

    // Determine how many links to open from start index
    const limit = maxLinks === 'all'
      ? resetLinks.length
      : Math.min(startIndex + parseInt(maxLinks), resetLinks.length);

    // Start the opening process
    openNextLink(startIndex, resetLinks, limit);
  };

  // Reset progress and start from beginning
  const resetAndStart = () => {
    setLastOpenedIndex(0);

    // Reset all links to "unopened" state
    const resetLinks = selectedLinks.map(link => ({
      ...link,
      opened: false
    }));
    setSelectedLinks(resetLinks);

    startRateLimitedOpening(0);
  };

  // Recursive function to open links with delay
  const openNextLink = (index, links, limit) => {
    if (!links || index >= links.length || index >= limit) {
      // All links opened (or reached limit)
      setOpeningLinks(false);
      setLastOpenedIndex(index); // Save where we stopped
      return;
    }

    // Open current link
    handleOpenSingle(links[index].link, index);
    setCurrentLinkIndex(index);

    // Schedule next link opening
    setTimeout(() => {
      openNextLink(index + 1, links, limit);
    }, openDelay * 1000); // Convert seconds to milliseconds
  };

  // Calculate progress percentage based on limit
  const getTotalToOpen = () => {
    if (maxLinks === 'all') return selectedLinks.length;
    return Math.min(parseInt(maxLinks), selectedLinks.length);
  };

  const progressPercentage = openingLinks && getTotalToOpen() > 0
    ? Math.round((currentLinkIndex / getTotalToOpen()) * 100)
    : 0;

  // Don't render if no links of this type exist
  if (linkCount === 0) {
    return null;
  }

  return (
    <>
      <motion.button
        onClick={handleShowLinks}
        className="mb-3 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <ExternalLink className="w-4 h-4" />
        {`Open All ${label} Links (${linkCount})`}
      </motion.button>

      <AnimatePresence>
        {showLinkList && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => !openingLinks && setShowLinkList(false)}
          >
            <motion.div
              ref={modalRef}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              className="bg-white rounded-xl p-4 w-full max-w-2xl max-h-[80vh] overflow-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-blue-900">
                  {`${label} Links (${selectedLinks.length})`}
                </h3>
                {!openingLinks && (
                  <button 
                    onClick={() => setShowLinkList(false)}
                    className="p-1 hover:bg-gray-100 rounded-full"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mb-4 flex flex-wrap gap-2">
                {!openingLinks ? (
                  <>
                    <button
                      onClick={handleCopyAllLinks}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                    >
                      {copied ? (
                        <>
                          <CheckCheck className="w-4 h-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy All Links
                        </>
                      )}
                    </button>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Open</span>
                      <select
                        value={maxLinks}
                        onChange={(e) => setMaxLinks(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-sm font-medium"
                      >
                        <option value="5">First 5</option>
                        <option value="10">First 10</option>
                        <option value="15">First 15</option>
                        <option value="20">First 20</option>
                        <option value="25">First 25</option>
                        <option value="all">All ({selectedLinks.length})</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">every</span>
                      <select
                        value={openDelay}
                        onChange={(e) => setOpenDelay(Number(e.target.value))}
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                      >
                        <option value="1">1 second</option>
                        <option value="2">2 seconds</option>
                        <option value="3">3 seconds</option>
                        <option value="5">5 seconds</option>
                        <option value="10">10 seconds</option>
                      </select>
                    </div>
                    
                    {lastOpenedIndex > 0 && lastOpenedIndex < selectedLinks.length ? (
                      <>
                        <button
                          onClick={() => startRateLimitedOpening(lastOpenedIndex)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Continue from #{lastOpenedIndex + 1}
                        </button>
                        <button
                          onClick={resetAndStart}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Start from Beginning
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => startRateLimitedOpening(0)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Start Opening Links
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    onClick={() => setOpeningLinks(false)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Stop Opening
                  </button>
                )}
              </div>

              {/* Progress Bar (only shown when opening links) */}
              {openingLinks && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-blue-700">
                      Opening link {currentLinkIndex + 1} of {getTotalToOpen()}
                      {maxLinks !== 'all' && <span className="text-gray-500"> (limited)</span>}
                    </span>
                    <span className="text-blue-700 font-medium">{progressPercentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-center mt-2">
                    <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                  </div>
                </div>
              )}

              {/* Information Alert */}
              <div className="border-t border-gray-200 pt-3">
                <div className="text-sm text-yellow-600 bg-yellow-50 p-3 rounded-lg mb-4 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p>
                    {openingLinks 
                      ? `Opening one link every ${openDelay} seconds to avoid rate limiting. Please don't close this dialog.`
                      : "Use the rate-limited opener to avoid 429 errors. You can also click links individually or copy all links to open them later."
                    }
                  </p>
                </div>

                {/* Link List */}
                <div className="space-y-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {selectedLinks.map((item, index) => (
                    <div 
                      key={index} 
                      className={`p-3 rounded-lg flex items-center gap-3 ${
                        item.opened 
                          ? 'bg-green-50 border border-green-100' 
                          : index === currentLinkIndex && openingLinks
                            ? 'bg-blue-50 border border-blue-100'
                            : 'bg-gray-50'
                      }`}
                    >
                      {item.opened && (
                        <CheckCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                      )}
                      {!item.opened && index === currentLinkIndex && openingLinks && (
                        <Loader2 className="w-4 h-4 text-blue-600 animate-spin flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-blue-900 mb-1 truncate">{item.company}</div>
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline text-sm truncate block"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenSingle(item.link, index);
                          }}
                        >
                          {item.link.length > 50 ? `${item.link.substring(0, 50)}...` : item.link}
                        </a>
                      </div>
                      <button
                        onClick={() => handleOpenSingle(item.link, index)}
                        className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg flex-shrink-0"
                        title="Open link"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default OpenAllLinksButton;